import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { CSSTransition } from "react-transition-group";
import { useKeyDown } from "../../utils/hooks/keydown";
import BackDrop from "../BackDrop";
import Card from "../Card";
import styles from "./Modal.module.scss";

import type { CardProps } from "../Card/types";

export type ModalProps = CardProps & {
  isOpen: boolean;
  open(): void;
  close(): void;
  onOpen?: (cb: (...args: any[]) => void) => void;
  onClose?: (cb: (...args: any[]) => void) => void;
  fullscreen?: boolean;
  fullscreenOnMobile?: boolean;
  bottomSheetOnMobile?: boolean;
  unmountOnExit?: boolean;
  backdropClass?: string;
};

export const useModal = () => {
  const [open, setOpen] = useState(false);

  const onCloseCB = useRef<(...args: any[]) => void>();
  const onOpenCB = useRef<(...args: any[]) => void>();

  return {
    isOpen: open,
    open() {
      setOpen(true);
      onOpenCB.current?.call(onOpenCB.current);
    },
    close() {
      setOpen(false);
      onCloseCB.current?.call(onOpenCB.current);
    },
    onOpen(cb: (...args: any[]) => void) {
      onOpenCB.current = cb;
    },
    onClose(cb: (...args: any[]) => void) {
      onCloseCB.current = cb;
    }
  };
};

const Modal = (props: ModalProps) => {
  const {
    fullscreen,
    fullscreenOnMobile,
    bottomSheetOnMobile,
    isOpen,
    close,
    backdropClass = "",
    unmountOnExit = false,
    ...cardProps
  } = props;

  useKeyDown(close, { code: "Escape" });

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "initial";

    return () => {
      document.body.style.overflow = "initial";
    };
  }, [isOpen]);

  return (
    <BackDrop open={isOpen} onClick={close} className={backdropClass}>
      <CSSTransition
        in={isOpen}
        unmountOnExit={unmountOnExit}
        classNames={{
          enterDone: styles["modal--enter-done"],
          exit: styles["modal--exit"]
        }}
        timeout={{ enter: 0, exit: 300 }}
        appear
      >
        <Card
          {...cardProps}
          className={clsx(
            cardProps.className,
            styles["modal"],
            fullscreen && styles["modal--fullscreen"],
            fullscreenOnMobile && styles["modal--fullscreen-on-mobile"],
            bottomSheetOnMobile && styles["modal--bottomsheet-on-mobile"]
          )}
          onClick={(e) => e.stopPropagation()}
        />
      </CSSTransition>
    </BackDrop>
  );
};

export default Modal;
